/**
 * SPA navigation watcher: fires a callback after the page URL changes under a
 * live widget (pushState/replaceState/popstate, plus the Navigation API where
 * it exists for routers that captured the native methods before we loaded).
 *
 * Module-level singleton on purpose. The wrappers close over nothing but this
 * module's cell, so a wrapper stranded by a later patch from another script
 * (analytics and routers wrap history too) retains one nulled variable, not a
 * disposed store. A second mount replaces the callback instead of stacking a
 * second wrapper, so an orphaned widget (torn down without dispose) can never
 * keep firing.
 */
let onChange: (() => void) | null = null;
let installed = false;
let originalPush: History["pushState"] | null = null;
let originalReplace: History["replaceState"] | null = null;
let wrappedPush: History["pushState"] | null = null;
let wrappedReplace: History["replaceState"] | null = null;

const fire = (): void => {
  // Deferred: the callback tears down and restores page-bound UI, and that
  // work must not run inside the host router's own navigation call stack -
  // neither its cost nor its exceptions belong to the host.
  setTimeout(() => {
    try {
      onChange?.();
    } catch (error) {
      console.error("[PreviewLoop] navigation handler failed", error);
    }
  }, 0);
};

type NavigationLike = { addEventListener: (type: string, cb: () => void) => void };

const install = (): void => {
  originalPush = history.pushState;
  originalReplace = history.replaceState;
  wrappedPush = function (this: History, ...args: Parameters<History["pushState"]>) {
    originalPush!.apply(this, args);
    fire();
  };
  wrappedReplace = function (this: History, ...args: Parameters<History["replaceState"]>) {
    originalReplace!.apply(this, args);
    fire();
  };
  history.pushState = wrappedPush;
  history.replaceState = wrappedReplace;
  window.addEventListener("popstate", fire);
  // Chromium's Navigation API sees traversals that bypass the patched methods.
  (window as { navigation?: NavigationLike }).navigation?.addEventListener(
    "currententrychange",
    fire,
  );
  installed = true;
};

/** Watch for SPA navigations. Returns an unwatch that detaches the callback
    and restores the history methods when nothing wrapped over them since. */
export function watchPageNavigation(handler: () => void): () => void {
  onChange = handler;
  if (!installed) install();
  return () => {
    if (onChange !== handler) return;
    onChange = null;
    // Another script may have wrapped after us; only undo our own layer.
    // A stranded wrapper only forwards and fires into the nulled cell.
    if (history.pushState === wrappedPush && originalPush) {
      history.pushState = originalPush;
    }
    if (history.replaceState === wrappedReplace && originalReplace) {
      history.replaceState = originalReplace;
    }
    if (history.pushState !== wrappedPush && history.replaceState !== wrappedReplace) {
      window.removeEventListener("popstate", fire);
      installed = false;
    }
  };
}
